import { useState } from 'react';
import { useLocaleNavigate } from '../i18n/locale-link';
import { useT } from '../i18n/locale-context';
import { useAuth } from '../lib/auth-context';
import { downloadCatalogExport, type CatalogFilters } from '../lib/catalog-client';
import { DownloadIcon } from './icons';
import { cn } from '@/lib/utils';

/**
 * Pro-gated "Export to Excel" button. Shown only to signed-in users; on the
 * server-side `pro_required` wall it routes to /plans. Downloads the current
 * filtered result set as .xlsx (honours the active catalog filters).
 *
 * `compact` renders an icon-only button (used on the mobile filter row where the
 * export shares a line with the filter/sort triggers — no extra row).
 */
export function ExportButton({
  filters,
  download,
  compact = false,
  className,
}: {
  filters?: CatalogFilters;
  /** A different export behind the same button, gate and walls — /results
   *  passes its winners export (`downloadResultsExport`). Must reject with the
   *  API's error code (`pro_required` → /plans) like the lots export does. */
  download?: () => Promise<void>;
  compact?: boolean;
  className?: string;
}) {
  const t = useT();
  const { status } = useAuth();
  const navigate = useLocaleNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (status !== 'authed') return null;

  async function handleClick() {
    setError(null);
    setBusy(true);
    try {
      await (download ? download() : downloadCatalogExport(filters ?? {}));
    } catch (e) {
      const code = e instanceof Error ? e.message : 'export_failed';
      if (code === 'pro_required') {
        navigate('/plans');
        return;
      }
      // Any non-paywall failure (network, 5xx) — don't mislabel it as a Pro wall.
      setError(t('catalog.exportFailed'));
    } finally {
      setBusy(false);
    }
  }

  // Shared look — matches the Фильтры / Sort controls (ink text on a light
  // surface, teal on hover), not a greyed-out disabled appearance.
  const base =
    'inline-flex items-center justify-center gap-1.5 rounded-md border border-edge bg-bone-surface font-semibold text-ink transition-colors hover:border-brand/40 hover:text-brand disabled:opacity-60';

  if (compact) {
    // Mobile filter row: a square, icon-only button (Фильтры + Sort take the
    // remaining width; this one stays a fixed square at the end of the row).
    return (
      <button
        type="button"
        onClick={() => void handleClick()}
        disabled={busy}
        aria-label={t('catalog.export')}
        title={error ?? t('catalog.export')}
        className={cn(base, 'h-11 w-11 shrink-0', className)}
      >
        {busy ? <span className="text-[15px]">…</span> : <DownloadIcon className="h-4 w-4" />}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => void handleClick()}
        disabled={busy}
        className={cn(base, 'h-11 px-3 text-[12px] md:h-9 md:text-[13px]', className)}
      >
        <DownloadIcon className="h-4 w-4" />
        {busy ? '…' : t('catalog.export')}
      </button>
      {error && <span className="text-[11.5px] text-ink-muted">{error}</span>}
    </div>
  );
}
